// src/hooks/useAuth.js
import { SKIP_AUTH, MOCK_USER } from '@/lib/config';
import { useState, useEffect } from 'react';
import { useAuth0 as useRealAuth0React } from '@auth0/auth0-react';
import { useAuth0 as useMockAuth0Hook } from '../components/auth/MockAuth0Provider';
import { registerTokenGetter } from '@/lib/authToken';
import { refreshAccessToken } from '@/services/apiService';

// Função auxiliar para decodificar o token JWT (nativa)
// atob() retorna uma string binária (1 char = 1 byte), então precisamos
// reinterpretar esses bytes como UTF-8 antes do JSON.parse — do contrário,
// caracteres acentuados (ex: "ç") viram mojibake ("Ã§").
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const binaryString = window.atob(base64);
    const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
    const jsonString = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

// margem de segurança: renova o token um pouco antes do exp real,
// pra não perder uma chamada de API por estar renovando "em cima da hora"
const EXPIRY_SKEW_SECONDS = 30;

const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 <= Date.now() + EXPIRY_SKEW_SECONDS * 1000;
};

const clearManualSession = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('refresh_token');
};

// Login por e-mail/senha (ROPC) não tem SDK cuidando de renovação — aqui é onde
// isso acontece: se o access_token salvo já expirou, tenta trocar o refresh_token
// por um par novo antes de devolver algo pra apiService anexar no header Authorization.
//
// refreshPromise garante um único refresh em voo por vez: sem isso, várias chamadas
// de API disparadas ao mesmo tempo (ex: dashboard carregando após a aba ficar inativa)
// chamavam refreshAccessToken() em paralelo com o mesmo refresh_token. Com rotação de
// refresh token ativada no Auth0, só a primeira chamada vale — as outras reusam um
// token já rotacionado e falham, e essa rajada de falhas foi o gatilho real do
// bloqueio de brute-force que impedia logins novos válidos logo em seguida.
let refreshPromise = null;

const getValidManualAccessToken = async () => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) return null;

  if (!isTokenExpired(accessToken)) {
    return accessToken;
  }

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    clearManualSession();
    return null;
  }

  if (!refreshPromise) {
    refreshPromise = refreshAccessToken(refreshToken)
      .then((data) => {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('id_token', data.id_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        return data.access_token;
      })
      .catch(() => {
        clearManualSession();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

/**
 * Hook unificado de autenticação.
 * Mantemos o nome 'useAuth0' para compatibilidade com o restante do projeto.
 */
export const useAuth0 = () => {
  // Chamamos o hook baseado em SKIP_AUTH. 
  // Apesar de condicional ser uma prática ruim em hooks, SKIP_AUTH é imutável em tempo de execução.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth0React = SKIP_AUTH ? useMockAuth0Hook() : useRealAuth0React();

  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      if (SKIP_AUTH) {
        // Modo Mock
        setUser(MOCK_USER);
        setIsAuthenticated(true);
        setIsLoading(false);
      } else {
        
        const idToken = localStorage.getItem('id_token');
        const accessToken = localStorage.getItem('access_token');

        if (idToken && accessToken) {
          
          const decoded = decodeToken(idToken);
          if (decoded) {
            setUser({
              name: decoded.name || decoded.email,
              email: decoded.email,
              picture: decoded.picture,
              sub: decoded.sub,
              email_verified: decoded.email_verified,
              updated_at: decoded.updated_at
            });
            setIsAuthenticated(true);

          } else {
            // Token inválido
            localStorage.removeItem('id_token');
            localStorage.removeItem('access_token');
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
        setIsLoading(false);
      }
    };

    checkAuth();
    
    // Escuta mudanças no localStorage (útil se o login abrir em outra aba, por exemplo)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const logout = () => {
    clearManualSession();
    if (!SKIP_AUTH && auth0React && auth0React.isAuthenticated) {
        auth0React.logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
        window.location.href = '/login';
    }
  };

  const unifiedIsAuthenticated = (auth0React && auth0React.isAuthenticated) || isAuthenticated;
  const unifiedUser = (auth0React && auth0React.isAuthenticated) ? auth0React.user : user;
  const unifiedIsLoading = auth0React ? (auth0React.isLoading || isLoading) : isLoading;

  const getAccessTokenSilently = async (...args) => {
    if (auth0React && auth0React.isAuthenticated) {
        return auth0React.getAccessTokenSilently(...args);
    }
    return getValidManualAccessToken();
  };

  // Permite que apiService.js (fora da árvore React) obtenha o token atual
  // para anexar o header Authorization em toda chamada à API.
  registerTokenGetter(getAccessTokenSilently);

  return {
    user: unifiedUser,
    isAuthenticated: unifiedIsAuthenticated,
    isLoading: unifiedIsLoading,
    logout,
    loginWithRedirect: auth0React ? auth0React.loginWithRedirect : () => window.location.href = '/login',
    getAccessTokenSilently
  };
};
